<script setup lang="ts">
// The participant's history (RDT-319), mirroring what the trainer sees in the project so support can
// answer a ticket without impersonating the trainer. The labels reuse the shared
// `project-details.participants.activity.*` keys, so both apps name the events identically and no new
// translations are needed.
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { ProfileAdministrationAPI } from "../../api";
import { ParticipantActivity } from "../../types/PersologOnlineTypes.ts";
import { formatDateTime } from "../../utils/dateFormat.ts";

const props = defineProps({
    participantId: {
        type: Number,
        required: true,
    },
});

const { t, te } = useI18n();

const activities = ref<ParticipantActivity[]>([]);
const loading = ref(false);
const failed = ref(false);

const load = async () => {
    loading.value = true;
    failed.value = false;
    try {
        const events = await ProfileAdministrationAPI.getParticipantActivity(props.participantId);
        // Oldest first, so the list reads as a history.
        activities.value = [...events].sort((a, b) => +new Date(a.date) - +new Date(b.date));
    } catch (error) {
        // Reported inline rather than as a toast: the row stays open, and a 403 here simply means the
        // widened admin authorization is not deployed yet.
        console.error("Could not load the activities of the participant", error);
        activities.value = [];
        failed.value = true;
    } finally {
        loading.value = false;
    }
};

/** Falls back to the raw type so an activity the labels do not cover yet is still visible. */
const label = (activity: ParticipantActivity) => {
    const key = `project-details.participants.activity.${activity.type.toLowerCase()}`;
    return te(key) ? t(key, { language: activity.profileLanguage?.toUpperCase() ?? "" }) : activity.type;
};

watch(() => props.participantId, load, { immediate: true });
</script>

<template>
    <div :data-cy="`participant-activities-${participantId}`">
        <h4>{{ t("project-details.participants.activity.show") }}</h4>

        <div v-if="loading" class="flex items-center gap-2">
            <i class="pi pi-spin pi-spinner"></i>
        </div>
        <Message v-else-if="failed" severity="error" :closable="false">
            {{ t("messages.load.error") }}
        </Message>
        <span v-else-if="!activities.length">
            {{ t("project-details.participants.activity.empty") }}
        </span>
        <Timeline v-else :value="activities">
            <template #opposite="slotProps">
                <small class="p-text-secondary">{{ formatDateTime(slotProps.item.date) }}</small>
            </template>
            <template #content="slotProps">
                {{ label(slotProps.item) }}
            </template>
        </Timeline>
    </div>
</template>
